import type { AppRole, AuthUser, NavigationItem } from '@360dash/shared'
import {
  Activity,
  Brain,
  BarChart3,
  Bell,
  Boxes,
  CalendarDays,
  CheckSquare,
  FileText,
  FolderOpen,
  Gift,
  GitBranch,
  HelpCircle,
  LayoutDashboard,
  Lock,
  Mail,
  MonitorUp,
  Receipt,
  RefreshCw,
  Rocket,
  Settings,
  Shield,
  Sparkles,
  Zap,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { getTenantConfig, isModuleEnabled } from './tenant-config'

const iconMap: Record<string, LucideIcon> = {
  activity: Activity,
  alerts: Bell,
  approvals: CheckSquare,
  'ai-blog': Sparkles,
  blog: FileText,
  bookings: CalendarDays,
  changes: RefreshCw,
  clients: Users,
  community: Users,
  'change-password': Lock,
  dashboard: LayoutDashboard,
  faq: HelpCircle,
  files: FolderOpen,
  github: GitBranch,
  insights: BarChart3,
  invoices: Receipt,
  newsletter: Mail,
  onboard: Rocket,
  'ops-brain': Brain,
  performance: Zap,
  sites: MonitorUp,
  perks: Gift,
  referrals: RefreshCw,
  resources: FileText,
  security: Shield,
  settings: Settings,
  systems: Boxes,
  moderation: Shield,
  vault: Lock,
}

const NAV_ITEMS: Record<AppRole, NavigationItem[]> = {
  admin: [
    { id: 'dashboard', label: 'Dashboard', href: '/admin', icon: 'dashboard', permission: 'admin:dashboard', mobilePrimary: true },
    { id: 'clients', label: 'Clients', href: '/admin/clients', icon: 'clients', permission: 'admin:dashboard' },
    { id: 'onboard', label: 'Onboard', href: '/admin/onboard', icon: 'onboard', permission: 'admin:dashboard' },
    { id: 'approvals', label: 'Approvals', href: '/admin/approvals', icon: 'approvals', permission: 'admin:dashboard' },
    { id: 'files', label: 'Files', href: '/admin/files', icon: 'files', permission: 'admin:dashboard' },
    { id: 'activity', label: 'Activity', href: '/admin/activity', icon: 'activity', permission: 'admin:dashboard' },
    { id: 'invoices', label: 'Invoices', href: '/admin/invoices', icon: 'invoices', permission: 'admin:dashboard' },
    { id: 'analytics', label: 'Site Analytics', href: '/admin/analytics', icon: 'insights', permission: 'admin:dashboard' },
    { id: 'change-requests', label: 'Change Requests', href: '/admin/change-requests', icon: 'changes', permission: 'admin:dashboard' },
    { id: 'newsletters', label: 'Newsletters', href: '/admin/newsletters', icon: 'newsletter', permission: 'admin:dashboard' },
    { id: 'ai-blog', label: 'AI Blog Writer', href: '/admin/ai-blog', icon: 'ai-blog', permission: 'admin:dashboard' },
    { id: 'blog', label: 'Blog (Legacy)', href: '/admin/blog', icon: 'blog', permission: 'admin:dashboard' },
    { id: 'moderation', label: 'Moderation', href: '/admin/moderation', icon: 'moderation', permission: 'admin:dashboard' },
    { id: 'performance', label: 'Performance', href: '/admin/performance', icon: 'performance', permission: 'admin:dashboard' },
    { id: 'security', label: 'Security', href: '/admin/security', icon: 'security', permission: 'admin:dashboard' },
    { id: 'ops-brain', label: 'Ops Brain', href: '/admin/ops-brain', icon: 'ops-brain', permission: 'admin:dashboard' },
    { id: 'systems', label: 'Systems', href: '/admin/systems', icon: 'systems', permission: 'admin:dashboard' },
    { id: 'sites', label: 'Managed Sites', href: '/admin/sites', icon: 'sites', permission: 'admin:dashboard' },
    { id: 'github', label: 'GitHub Ops', href: '/admin/github', icon: 'github', permission: 'admin:dashboard' },
  ],
  client: [
    { id: 'dashboard', label: 'Dashboard', href: '/portal', icon: 'dashboard', permission: 'own:read', mobilePrimary: true },
    { id: 'analytics', label: 'Analytics', href: '/portal/insights', icon: 'insights', permission: 'own:read', mobilePrimary: true },
    { id: 'publishing', label: 'Publishing', href: '/portal/publishing', icon: 'newsletter', permission: 'newsletter:read', mobilePrimary: true },
    { id: 'bookings', label: 'Bookings', href: '/portal/bookings', icon: 'bookings', permission: 'own:read', mobilePrimary: true },
    { id: 'changes', label: 'Changes', href: '/portal/changes', icon: 'changes', permission: 'change:read', mobilePrimary: true },
    { id: 'referrals', label: 'Refer', href: '/portal/referrals', icon: 'referrals', permission: 'referral:read' },
    { id: 'invoices', label: 'Invoices', href: '/portal/invoices', icon: 'invoices', permission: 'invoice:read' },
    { id: 'resources', label: 'Resources', href: '/portal/resources', icon: 'resources', permission: 'own:read' },
    { id: 'community', label: 'Community', href: '/portal/community', icon: 'community', permission: 'community:read' },
    { id: 'perks', label: 'Perks', href: '/portal/perks', icon: 'perks', permission: 'perk:read' },
    { id: 'vault', label: 'Documents', href: '/portal/vault', icon: 'vault', permission: 'own:read' },
    { id: 'faq', label: 'FAQ', href: '/portal/faq', icon: 'faq', permission: 'own:read' },
    { id: 'recent-activity', label: 'Recent Activity', href: '/portal/alerts', icon: 'activity', permission: 'alert:read' },
    { id: 'settings', label: 'Settings', href: '/portal/settings', icon: 'settings', permission: 'own:update' },
  ],
}

export function hasPermission(user: AuthUser | null, permission?: string): boolean {
  if (!permission) return true
  if (!user) return false
  return user.permissions.includes('*') || user.permissions.includes(permission)
}

export function getNavigationItems(role: AppRole, user: AuthUser | null): NavigationItem[] {
  return NAV_ITEMS[role].filter((item) => isModuleEnabled(item.moduleKey) && hasPermission(user, item.permission))
}

export function getMobilePrimaryItems(user: AuthUser | null): NavigationItem[] {
  return getNavigationItems('client', user).filter((item) => item.mobilePrimary).slice(0, 4)
}

export function getOverflowItems(user: AuthUser | null): NavigationItem[] {
  const primaryIds = new Set(getMobilePrimaryItems(user).map((item) => item.id))
  return getNavigationItems('client', user).filter((item) => !primaryIds.has(item.id))
}

export function getNavigationIcon(icon: string): LucideIcon {
  return iconMap[icon] ?? LayoutDashboard
}

export function getProductName(): string {
  return getTenantConfig().branding.product_name
}
